// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/utils/memberSearchesApi.js
//
// Appels HTTP du module « Recherches & entraide pro » (lot F1). Un seul endroit connaît les
// chemins : les écrans n'écrivent jamais d'URL. Routeur monté sur `/api/member-searches`
// (pluriel, avec tiret) ; apiClient préfixe déjà `/api`.
//
// Chaque fonction renvoie le corps JSON de l'API tel quel ; les erreurs sont celles d'apiClient
// (lues avec `parseApiError`).

import { get, post, put } from './apiClient'
import { buildListQuery } from './memberSearches'

const BASE = '/member-searches'

/** Plafonds, périmètre proposé, motifs de fermeture (source de vérité : le backend). */
export const fetchConfig = () => get(`${BASE}/config`)

/** Recherches du réseau visibles par le membre. */
export const fetchNetworkSearches = (filters, paging) => {
  const query = buildListQuery(filters, paging)
  return get(query ? `${BASE}?${query}` : BASE)
}

/** Mes recherches, tous statuts. `status` optionnel : 'active' | 'expired' | 'closed'. */
export const fetchMySearches = ({ status, page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (page > 1) params.set('page', String(page))
  if (limit !== 20) params.set('limit', String(limit))
  const query = params.toString()
  return get(query ? `${BASE}/mine?${query}` : `${BASE}/mine`)
}

export const fetchSearch = (id) => get(`${BASE}/${encodeURIComponent(id)}`)

export const createSearch = (payload) => post(BASE, payload)

export const updateSearch = (id, payload) => put(`${BASE}/${encodeURIComponent(id)}`, payload)

export const renewSearch = (id) => post(`${BASE}/${encodeURIComponent(id)}/renew`, {})

export const reactivateSearch = (id) => post(`${BASE}/${encodeURIComponent(id)}/reactivate`, {})

export const closeSearch = (id, reason) =>
  post(`${BASE}/${encodeURIComponent(id)}/close`, { reason })

// ─── Offres correspondantes (lot 4) ─────────────────────────────────────────
// Le consentement et la propriété sont vérifiés par le SERVEUR (403 / 404) : l'interface ne fait
// que proposer. Rien n'est envoyé au membre sans son action explicite (`notifyMatches`).

export const fetchMatchingConsent = () => get(`${BASE}/matching/consent`)

/** Octroi (true) ou retrait (false) du consentement au rapprochement recherches/offres. */
export const setMatchingConsent = (enabled) => put(`${BASE}/matching/consent`, { enabled })

export const fetchMatches = (id, { page = 1, limit = 10 } = {}) => {
  const params = new URLSearchParams()
  if (page > 1) params.set('page', String(page))
  if (limit !== 10) params.set('limit', String(limit))
  const query = params.toString()
  const base = `${BASE}/${encodeURIComponent(id)}/matches`
  return get(query ? `${base}?${query}` : base)
}

/** « Prévenez-moi des correspondances que je n'ai pas encore vues » : action explicite du membre. */
export const notifyMatches = (id) => post(`${BASE}/${encodeURIComponent(id)}/matches/notify`, {})

// ─── Réglages administrateur du périmètre ───────────────────────────────────
// Le rôle administrateur est relu en base par le serveur (403 ADMIN_ONLY sinon).

export const fetchAdminSettings = () => get(`${BASE}/admin/settings`)

/** `changes` : uniquement les clés modifiées (defaultScope, allowWiden, reminderEmail). */
export const saveAdminSettings = (changes) => put(`${BASE}/admin/settings`, changes)

/** Journal des changements de réglages (plus récent d'abord), paginé. */
export const fetchAdminSettingsHistory = ({ page = 1, limit = 10 } = {}) => {
  const params = new URLSearchParams()
  if (page > 1) params.set('page', String(page))
  if (limit !== 20) params.set('limit', String(limit))
  const query = params.toString()
  return get(query ? `${BASE}/admin/settings/history?${query}` : `${BASE}/admin/settings/history`)
}
